import { Brain, ArrowRightLeft, CalendarDays, ClipboardList } from 'lucide-react';

/*
 * Baylor St. Luke's Neuroanesthesia rotation. Built from the Blackboard BSLMC Neuro
 * Rotation Orientation (Dr Brownewell). See docs/blackboard-bslmc.md.
 */

export const BB_DOC = (id) => `https://bcm.blackboard.com/ultra/courses/_129_1/document/${id}?view=content&state=view`;
const NEURO = BB_DOC('_492468_1');

export const bslmcNeuro = {
  accent: '#2596be',
  icon: Brain,
  tagline: 'Craniotomies, aneurysms, DBS, neuro IR',
  notesKey: 'notes:st-lukes:neuro',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'handoff', title: 'ICU Handoff', subtitle: 'The call, the note, the aid', icon: ArrowRightLeft, action: 'guide:handoff', featured: true },
    ] },
    { title: 'Logistics', tools: [
      { id: 'day', title: 'Your Day', subtitle: 'Setup, start times, controlled substances', icon: CalendarDays, action: 'guide:day' },
      { id: 'start', title: 'Getting Started', subtitle: 'Badge, Epic, first morning', icon: ClipboardList, action: 'guide:start' },
    ] },
  ],

  contacts: {
    storageKey: 'bslmc-neuro-favorites',
    groups: [
      { id: 'neuro', title: 'Neuro', open: true, items: [
        { label: 'Neurosurgery resident', phone: '8323556878', note: 'ICU report before transport' },
        { label: 'GME office', phone: '8323554200', note: 'Badge and Epic. G131, 6720 Bertner. Weekdays 08:00–13:00 and 14:00–15:30' },
      ] },
    ],
  },

  locations: {
    groups: [
      { id: 'or', title: 'Main OR', items: [
        { name: 'Anesthesia office', floor: '2nd floor', where: 'Behind the OR front desk, yellow elevators', note: 'Meet here at 06:30 on day one' },
        { name: 'Holding', floor: '2nd floor', note: 'First cases preop in the PACU' },
        { name: 'OR-to-ICU handoff aid', floor: 'Every OR', where: 'On top of the blue cart' },
      ] },
      { id: 'gme', title: 'Elsewhere', items: [
        { name: 'GME office', floor: 'G131 BSLMC', where: '6720 Bertner Avenue', note: 'Bring your Baylor ID' },
      ] },
    ],
  },

  documents: {
    storageKey: 'bslmc-neuro-pinned-docs',
    groups: [
      { id: 'neuro', title: 'Neuro', items: [
        { title: 'Neuro rotation orientation', subtitle: 'Blackboard', url: NEURO },
      ] },
    ],
  },

  guides: {
    handoff: {
      title: 'ICU Handoff',
      sections: [
        { title: 'At the timeout', blocks: [{ type: 'bullets', items: [
          'Confirm where the patient goes after surgery',
        ] }] },
        { title: 'Going to the ICU', blocks: [{ type: 'steps', items: [
          'Call the neurosurgery resident and give report before transport',
          'ICU handoff note in Epic: Notes, New note, .icuhandoff, F2 through the fields',
          'End the note with the name of the ICU resident or midlevel who took report',
          'The OR-to-ICU handoff aid on the blue cart works for intraoperative handoffs too',
        ] }] },
      ],
      sources: [{ title: 'BSLMC neuro rotation orientation', href: NEURO, detail: 'Blackboard, sign-in required' }],
    },

    day: {
      title: 'Your Day',
      sections: [
        { title: 'Morning', blocks: [{ type: 'bullets', items: [
          'In by 06:30 with the room set up, lines and infusions included',
          'Craniotomy rooms usually get an arterial line and Hotline from the techs. If not, call them; their five-digit number is on top of the machine',
          'Preop in holding, or the PACU for first starts, then meet the attending',
          'Most cases start at 07:30 and an on-time start is mandatory. Be there by 07:00 anyway; an emergent stroke can post',
        ] }] },
        { title: 'Controlled substances', blocks: [{ type: 'bullets', items: [
          { text: 'Locked in the Pyxis whenever you leave the OR', warn: true },
          'Sufentanil, remifentanil and propofol drips may be spiked but never left on the pump unattended',
        ] }] },
        { title: 'The cases', blocks: [{ type: 'bullets', items: [
          'Tumor craniotomies, aneurysm clipping and other vascular work, deep brain stimulators',
          'Interventional neuroradiology: angiography and embolisation',
          'Discuss every case with the attending the day before. The considerations are unusual',
        ] }] },
        { title: 'Hours', blocks: [{ type: 'bullets', items: [
          'Usually out by 17:00, often earlier, sometimes later. Plan for it',
          'Need to leave at a set time or want relief for an appointment: tell the attending 48 hours ahead',
          'Grand Rounds Wednesdays, Senior Seminar Tuesdays for CA-3s. Straight to the OR after',
          'Time off requests a month before the rotation',
          'Sick or an emergency: tell the attending at once',
        ] }] },
      ],
      sources: [{ title: 'BSLMC neuro rotation orientation', href: NEURO, detail: 'Blackboard, sign-in required' }],
    },

    start: {
      title: 'Getting Started',
      sections: [
        { title: 'Before the rotation', blocks: [{ type: 'bullets', items: [
          'A valid St. Luke’s badge and Epic access. Recent THI residents already have both',
          'Otherwise the GME office at G131 with your Baylor ID, weekdays 08:00–13:00 and 14:00–15:30',
        ] }] },
        { title: 'First morning', blocks: [{ type: 'bullets', items: [
          '06:30 in the anesthesia office behind the OR front desk, 2nd floor by the yellow elevators',
          'OR tour, Epic and Pyxis checked, then a room that same day',
          'You receive the phone list, including the BSLMC attendings, and the .icuhandoff template',
        ] }] },
      ],
      sources: [{ title: 'BSLMC neuro rotation orientation', href: NEURO, detail: 'Blackboard, sign-in required' }],
    },
  },
};
